/**
 * IosUniversalLinksPage — /tracking/ios-universal-links (feature #178).
 *
 * Operator surface for configuring iOS Universal Link handoff per tracking
 * domain. The backend lives at /api/v1/ios-universal-links; this page:
 *
 *   1. Scope input — operator types a tracking domain (e.g. go.brand.example)
 *      to filter the list. Empty string = all domains in the tenant.
 *   2. Create form — domain, Apple Team ID, Bundle ID, label, AASA path
 *      patterns. Client-side validation mirrors the server regex so bad
 *      drafts never leave the browser.
 *   3. Apps table — grouped by domain. Each row shows Team.Bundle identifier,
 *      label, path patterns, AASA serve counter, last served timestamp, and
 *      actions (pause/activate, delete).
 *   4. Live AASA preview — pulls the rendered document for the selected
 *      domain so the operator can verify the JSON Apple's crawler will see
 *      WITHOUT tripping serve counters.
 *
 * Brand: emerald -> sky gradient headline, Inter, zinc borders, 6-8 px
 * rounded surfaces, light theme only. No emojis, no competitor names.
 *
 * Mirrors the "self-contained, no react-query" pattern used by other feature
 * pages so tests can drive a react-dom/server smoke without a DOM lib.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
	aasaUrl,
	appDraftSchema,
	appIdentifier,
	buildAASAFromRows,
	countByStatus,
	createApp,
	deleteApp,
	EMPTY_DRAFT,
	formatTimestamp,
	groupByDomain,
	type AppDraft,
	type AASADocument,
	type IosUniversalLinkAppView,
	type IosUniversalLinkStatus,
	listApps,
	patchApp,
	removeFromList,
	renderAASAPreview,
	replaceInList,
	sortApps,
	statusBadgeClass,
	STATUS_LABELS,
} from "./api";

// ---------------------------------------------------------------------------
// Brand tokens
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";
const BTN_PRIMARY =
	"inline-flex items-center justify-center rounded-[6px] border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_SECONDARY =
	"inline-flex items-center justify-center rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const HEADLINE =
	"bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-transparent";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface IosUniversalLinksPageProps {
	initialDomain?: string;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function IosUniversalLinksPage(props: IosUniversalLinksPageProps = {}) {
	const [scope, setScope] = useState<string>(props.initialDomain ?? "");
	const [scopeDraft, setScopeDraft] = useState<string>(
		props.initialDomain ?? "",
	);
	const [apps, setApps] = useState<IosUniversalLinkAppView[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [listError, setListError] = useState<string | null>(null);

	const [draft, setDraft] = useState<AppDraft>({
		...EMPTY_DRAFT,
		domain: props.initialDomain ?? "",
	});
	const [draftErrors, setDraftErrors] = useState<Record<string, string>>({});
	const [submitting, setSubmitting] = useState<boolean>(false);
	const [submitError, setSubmitError] = useState<string | null>(null);

	const [actionId, setActionId] = useState<string | null>(null);
	const [selectedDomain, setSelectedDomain] = useState<string | null>(
		props.initialDomain ?? null,
	);
	const [notice, setNotice] = useState<string | null>(null);

	// ---------------------------------------------------------------------
	// Data hydration
	// ---------------------------------------------------------------------

	const reload = useCallback(async () => {
		setLoading(true);
		setListError(null);
		try {
			const rows = await listApps(scope.trim() || undefined);
			setApps(sortApps(rows));
		} catch (err) {
			setListError(
				err instanceof Error ? err.message : "Failed to load apps.",
			);
		} finally {
			setLoading(false);
		}
	}, [scope]);

	useEffect(() => {
		void reload();
	}, [reload]);

	const kpis = useMemo(() => countByStatus(apps), [apps]);
	const grouped = useMemo(() => groupByDomain(apps), [apps]);

	const selectedGroup = useMemo(() => {
		if (!selectedDomain) return null;
		return grouped.find((g) => g.domain === selectedDomain) ?? null;
	}, [grouped, selectedDomain]);

	const previewJson = useMemo(() => {
		if (!selectedGroup) return "";
		return renderAASAPreview(selectedGroup.apps);
	}, [selectedGroup]);

	const previewDocument: AASADocument | null = useMemo(() => {
		if (!selectedGroup) return null;
		return buildAASAFromRows(selectedGroup.apps);
	}, [selectedGroup]);

	// ---------------------------------------------------------------------
	// Handlers
	// ---------------------------------------------------------------------

	function onScopeSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setScope(scopeDraft.trim());
	}

	async function onCreate(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setSubmitError(null);
		const parsed = appDraftSchema.safeParse(draft);
		if (!parsed.success) {
			setDraftErrors(parsed.errors);
			return;
		}
		setDraftErrors({});
		setSubmitting(true);
		try {
			const created = await createApp(parsed.data);
			setApps((prev) => sortApps(replaceInList(prev, created)));
			setDraft({ ...EMPTY_DRAFT, domain: parsed.data.domain });
			setSelectedDomain(created.domain);
			setNotice(
				`Registered ${appIdentifier(created)} on ${created.domain}.`,
			);
		} catch (err) {
			setSubmitError(err instanceof Error ? err.message : "Create failed.");
		} finally {
			setSubmitting(false);
		}
	}

	async function onToggleStatus(row: IosUniversalLinkAppView) {
		const nextStatus: IosUniversalLinkStatus =
			row.status === "active" ? "paused" : "active";
		setActionId(row.id);
		setNotice(null);
		try {
			const updated = await patchApp(row.id, { status: nextStatus });
			setApps((prev) => sortApps(replaceInList(prev, updated)));
			setNotice(
				`${appIdentifier(updated)} is now ${STATUS_LABELS[updated.status].toLowerCase()}.`,
			);
		} catch (err) {
			setListError(
				err instanceof Error ? err.message : "Status change failed.",
			);
		} finally {
			setActionId(null);
		}
	}

	async function onDelete(row: IosUniversalLinkAppView) {
		setActionId(row.id);
		setNotice(null);
		try {
			const result = await deleteApp(row.id);
			if ("deleted" in result && result.deleted) {
				setApps((prev) => removeFromList(prev, row.id));
				setNotice(`${appIdentifier(row)} removed.`);
			} else if ("archived" in result && result.archived) {
				setApps((prev) => sortApps(replaceInList(prev, result.app)));
				setNotice(`${appIdentifier(row)} archived.`);
			}
		} catch (err) {
			setListError(
				err instanceof Error ? err.message : "Delete failed.",
			);
		} finally {
			setActionId(null);
		}
	}

	function patchDraft<K extends keyof AppDraft>(
		key: K,
		value: AppDraft[K],
	) {
		setDraft((prev) => ({ ...prev, [key]: value }));
		if (draftErrors[key as string]) {
			setDraftErrors((prev) => {
				const next = { ...prev };
				delete next[key as string];
				return next;
			});
		}
	}

	// ---------------------------------------------------------------------
	// Render
	// ---------------------------------------------------------------------

	return (
		<main className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10 sm:py-14">
			<header className="flex flex-col gap-2">
				<h1 className="text-3xl font-semibold tracking-tight">
					<span className={HEADLINE}>iOS Universal Links</span>
				</h1>
				<p className="max-w-3xl text-sm text-zinc-600">
					Route Trackerity tracking URLs straight into a native iOS app
					by hosting the apple-app-site-association JSON on your tracking
					domain. Register each app with its Apple Team ID, bundle
					identifier, and AASA path patterns. The file is served as
					application/json at
					<code className="ml-1 rounded-[6px] bg-zinc-100 px-1 py-0.5 font-mono text-xs text-zinc-800">
						/.well-known/apple-app-site-association
					</code>
					.
				</p>
			</header>

			<section className={`${CARD} p-5`}>
				<form
					onSubmit={onScopeSubmit}
					className="flex flex-col gap-3 sm:flex-row sm:items-end"
				>
					<div className="flex flex-col gap-1 sm:flex-1">
						<label className={LABEL} htmlFor="scope-domain">
							Scope by tracking domain
						</label>
						<input
							id="scope-domain"
							className={INPUT}
							placeholder="go.brand.example (leave blank for all)"
							value={scopeDraft}
							onChange={(e) => setScopeDraft(e.target.value)}
						/>
						<span className={HINT}>
							Only apps registered on this domain will be shown.
						</span>
					</div>
					<div className="flex gap-2">
						<button type="submit" className={BTN_PRIMARY}>
							Apply
						</button>
						<button
							type="button"
							className={BTN_SECONDARY}
							onClick={() => {
								setScopeDraft("");
								setScope("");
							}}
						>
							Clear
						</button>
					</div>
				</form>
			</section>

			<section className="grid grid-cols-1 gap-3 sm:grid-cols-3">
				<KpiCard label="Active" value={kpis.active} tone="emerald" />
				<KpiCard label="Paused" value={kpis.paused} tone="amber" />
				<KpiCard label="Archived" value={kpis.archived} tone="zinc" />
			</section>

			<section className={`${CARD} p-5`}>
				<h2 className="mb-4 text-base font-semibold text-zinc-900">
					Register app
				</h2>
				<form
					onSubmit={onCreate}
					className="grid grid-cols-1 gap-4 md:grid-cols-2"
				>
					<FieldText
						id="draft-domain"
						label="Tracking domain"
						hint="Hostname only (no scheme or path)."
						value={draft.domain}
						onChange={(v) => patchDraft("domain", v)}
						error={draftErrors.domain}
					/>
					<FieldText
						id="draft-team"
						label="Apple Team ID"
						hint="10 uppercase alphanumerics from Apple Developer."
						value={draft.teamId}
						onChange={(v) => patchDraft("teamId", v)}
						error={draftErrors.teamId}
					/>
					<FieldText
						id="draft-bundle"
						label="Bundle ID"
						hint="Reverse-DNS, lowercase (e.g. com.brand.app)."
						value={draft.bundleId}
						onChange={(v) => patchDraft("bundleId", v)}
						error={draftErrors.bundleId}
					/>
					<FieldText
						id="draft-label"
						label="Label"
						hint="Shown to operators; not exposed to Apple."
						value={draft.label}
						onChange={(v) => patchDraft("label", v)}
						error={draftErrors.label}
					/>
					<div className="flex flex-col gap-1 md:col-span-2">
						<label className={LABEL} htmlFor="draft-paths">
							AASA path patterns
						</label>
						<textarea
							id="draft-paths"
							className={`${INPUT} min-h-[80px]`}
							placeholder="/go/*, /promo/*, /redirect/*"
							value={draft.pathsText}
							onChange={(e) =>
								patchDraft("pathsText", e.target.value)
							}
						/>
						{draftErrors.pathsText ? (
							<span className={ERROR_TEXT}>
								{draftErrors.pathsText}
							</span>
						) : (
							<span className={HINT}>
								Comma or newline separated. Each entry must start
								with / or be the bare wildcard *. Default is *.
							</span>
						)}
					</div>
					<div className="flex items-center gap-3 md:col-span-2">
						<button
							type="submit"
							className={BTN_PRIMARY}
							disabled={submitting}
						>
							{submitting ? "Saving..." : "Register app"}
						</button>
						{submitError ? (
							<span className={ERROR_TEXT}>{submitError}</span>
						) : null}
					</div>
				</form>
			</section>

			{notice ? (
				<div className="rounded-[6px] border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
					{notice}
				</div>
			) : null}

			<section className={`${CARD} p-5`}>
				<div className="flex items-center justify-between">
					<h2 className="text-base font-semibold text-zinc-900">
						Registered apps
					</h2>
					<button
						type="button"
						className={BTN_SECONDARY}
						onClick={() => void reload()}
					>
						Refresh
					</button>
				</div>

				{loading ? (
					<p className="py-6 text-sm text-zinc-500">Loading...</p>
				) : listError ? (
					<p className="py-6 text-sm text-red-600">{listError}</p>
				) : grouped.length === 0 ? (
					<p className="py-6 text-sm text-zinc-500">
						No apps yet. Register one above to publish an AASA file.
					</p>
				) : (
					<div className="mt-3 flex flex-col gap-6">
						{grouped.map((group) => (
							<DomainGroup
								key={group.domain}
								group={group}
								selectedDomain={selectedDomain}
								onSelect={(d) =>
									setSelectedDomain(
										selectedDomain === d ? null : d,
									)
								}
								onToggle={onToggleStatus}
								onDelete={onDelete}
								actionId={actionId}
							/>
						))}
					</div>
				)}
			</section>

			{selectedGroup && previewDocument ? (
				<section className={`${CARD} p-5`}>
					<h2 className="text-base font-semibold text-zinc-900">
						AASA preview
					</h2>
					<p className={`mt-1 ${HINT}`}>
						Live document for{" "}
						<span className="font-mono text-zinc-700">
							{selectedGroup.domain}
						</span>
						. Paused and archived rows are excluded — Apple only routes
						URLs to apps listed under an active row.
					</p>
					<div className="mt-3 flex flex-col gap-3">
						<div>
							<div className={LABEL}>AASA URL</div>
							<code className="mt-1 block break-all rounded-[6px] border border-zinc-200 bg-zinc-50 p-3 font-mono text-xs text-zinc-800">
								{aasaUrl(selectedGroup.domain)}
							</code>
						</div>
						<div>
							<div className={LABEL}>apple-app-site-association</div>
							<pre className="mt-1 max-h-80 overflow-auto whitespace-pre-wrap break-words rounded-[6px] border border-zinc-200 bg-zinc-50 p-3 font-mono text-xs text-zinc-800">
								{previewJson}
							</pre>
						</div>
						<div className="flex flex-wrap gap-2 text-xs text-zinc-600">
							<span>
								Active apps:{" "}
								{previewDocument.applinks.details.length}
							</span>
							<span aria-hidden>|</span>
							<span>
								Rows on this domain: {selectedGroup.apps.length}
							</span>
						</div>
					</div>
				</section>
			) : null}
		</main>
	);
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface DomainGroupProps {
	group: { domain: string; apps: IosUniversalLinkAppView[] };
	selectedDomain: string | null;
	onSelect: (domain: string) => void;
	onToggle: (row: IosUniversalLinkAppView) => void | Promise<void>;
	onDelete: (row: IosUniversalLinkAppView) => void | Promise<void>;
	actionId: string | null;
}

function DomainGroup({
	group,
	selectedDomain,
	onSelect,
	onToggle,
	onDelete,
	actionId,
}: DomainGroupProps) {
	const isSelected = group.domain === selectedDomain;
	return (
		<div className="rounded-[6px] border border-zinc-200">
			<div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-200 bg-zinc-50/80 px-4 py-2">
				<div className="flex flex-col">
					<span className="font-mono text-sm text-zinc-800">
						{group.domain}
					</span>
					<span className="text-xs text-zinc-500">
						{group.apps.length} app{group.apps.length === 1 ? "" : "s"}{" "}
						registered
					</span>
				</div>
				<button
					type="button"
					className="inline-flex items-center justify-center rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
					onClick={() => onSelect(group.domain)}
				>
					{isSelected ? "Hide AASA preview" : "Show AASA preview"}
				</button>
			</div>
			<div className="overflow-x-auto">
				<table className="min-w-full text-left text-sm">
					<thead className="border-b border-zinc-200 text-xs uppercase tracking-wide text-zinc-500">
						<tr>
							<th className="px-4 py-2 font-medium">App ID</th>
							<th className="px-4 py-2 font-medium">Label</th>
							<th className="px-4 py-2 font-medium">Paths</th>
							<th className="px-4 py-2 font-medium">Serves</th>
							<th className="px-4 py-2 font-medium">Last served</th>
							<th className="px-4 py-2 font-medium">Status</th>
							<th className="px-4 py-2 font-medium">Actions</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-zinc-100">
						{group.apps.map((row) => (
							<tr key={row.id}>
								<td className="px-4 py-2 font-mono text-xs text-zinc-800">
									{appIdentifier(row)}
								</td>
								<td className="px-4 py-2 text-zinc-700">
									{row.label}
								</td>
								<td className="px-4 py-2 text-zinc-700">
									<div className="flex flex-wrap gap-1">
										{row.paths.slice(0, 4).map((p) => (
											<code
												key={p}
												className="rounded-[6px] border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 font-mono text-[11px] text-zinc-700"
											>
												{p}
											</code>
										))}
										{row.paths.length > 4 ? (
											<span className="text-[11px] text-zinc-500">
												+{row.paths.length - 4} more
											</span>
										) : null}
									</div>
								</td>
								<td className="px-4 py-2 tabular-nums text-zinc-700">
									{row.aasa_serve_count.toLocaleString()}
								</td>
								<td className="px-4 py-2 text-zinc-500">
									{formatTimestamp(row.last_served_at)}
								</td>
								<td className="px-4 py-2">
									<span
										className={`inline-flex items-center rounded-[6px] border px-2 py-0.5 text-xs font-medium ${statusBadgeClass(row.status)}`}
									>
										{STATUS_LABELS[row.status]}
									</span>
								</td>
								<td className="px-4 py-2">
									<div className="flex flex-wrap gap-1">
										{row.status !== "archived" ? (
											<button
												type="button"
												className="inline-flex items-center justify-center rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60"
												onClick={() => void onToggle(row)}
												disabled={actionId === row.id}
											>
												{row.status === "active"
													? "Pause"
													: "Activate"}
											</button>
										) : null}
										<button
											type="button"
											className="inline-flex items-center justify-center rounded-[6px] border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
											onClick={() => void onDelete(row)}
											disabled={actionId === row.id}
										>
											Delete
										</button>
									</div>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
}

interface KpiCardProps {
	label: string;
	value: number;
	tone: "emerald" | "amber" | "zinc";
}

function KpiCard({ label, value, tone }: KpiCardProps) {
	const toneClass =
		tone === "emerald"
			? "text-emerald-700"
			: tone === "amber"
				? "text-amber-700"
				: "text-zinc-700";
	return (
		<div className={`${CARD} p-4`}>
			<div className="text-xs font-medium uppercase tracking-wide text-zinc-500">
				{label}
			</div>
			<div
				className={`mt-1 text-2xl font-semibold tabular-nums ${toneClass}`}
			>
				{value.toLocaleString()}
			</div>
		</div>
	);
}

interface FieldTextProps {
	id: string;
	label: string;
	hint: string;
	value: string;
	onChange: (v: string) => void;
	error?: string;
}

function FieldText({
	id,
	label,
	hint,
	value,
	onChange,
	error,
}: FieldTextProps) {
	return (
		<div className="flex flex-col gap-1">
			<label className={LABEL} htmlFor={id}>
				{label}
			</label>
			<input
				id={id}
				className={INPUT}
				value={value}
				onChange={(e) => onChange(e.target.value)}
			/>
			{error ? (
				<span className={ERROR_TEXT}>{error}</span>
			) : (
				<span className={HINT}>{hint}</span>
			)}
		</div>
	);
}

export default IosUniversalLinksPage;
